import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Label,
  Input,
  Button,
  Spinner,
  Alert,
} from "reactstrap";
import Loader from "../../../Components/Common/Loader";
import { useFormik } from "formik";
import * as Yup from "yup";

// RTK Query hooks - adjust import path / names if your slice uses different names
import {
  useGetCategoryByIdQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useGetCategoriesQuery,
} from "slices/InventoryManagement/inventoryManagementSlice";

interface CategoryPayload {
  name: string;
  description?: string;
  priority?: number;
  account_number: string;
}

interface CategoryEditProps {
  categoryId?: string | number;
  compact?: boolean; // when true, omit breadcrumb/page wrapper (for modal)
  onSaved?: () => void; // optional callback parent can use to close modal / refresh
}

const CategoryEditView: React.FC<CategoryEditProps> = ({
  categoryId,
  compact = true,
  onSaved,
}) => {
  const isEditMode = categoryId !== undefined && categoryId !== null;
  const categoryIdStr = isEditMode ? String(categoryId) : undefined;

  const [saveError, setSaveError] = useState<string | null>(null);

  // fetch category detail when editing (skip when no id)
  const {
    data: categoryData,
    isFetching: fetchingCategory,
    refetch: refetchCategory,
  } = useGetCategoryByIdQuery(categoryIdStr as string, { skip: !isEditMode });

  // to refresh list after save
  const { refetch: refetchCategories } = useGetCategoriesQuery(undefined);

  const [createCategory, { isLoading: creating }] = useCreateCategoryMutation();
  const [updateCategory, { isLoading: updating }] = useUpdateCategoryMutation();

  const formik = useFormik<CategoryPayload>({
    enableReinitialize: true,
    initialValues: {
      name: "",
      description: "",
      priority: 1,
      account_number: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().trim().required("Please enter category name"),
      description: Yup.string().nullable(),
      priority: Yup.number()
        .min(1, "Priority must be between 1 and 3")
        .max(3, "Priority must be between 1 and 3")
        .required("Priority is required"),
      account_number: Yup.string()
        .trim()
        .required("Please enter account number"),
    }),
    onSubmit: async (values) => {
      setSaveError(null);
      try {
        if (isEditMode && categoryId !== undefined) {
          const idNumber = Number(categoryId);
          const idToSend = Number.isNaN(idNumber) ? categoryIdStr : idNumber;
          // update call shape follows your measurement pattern: { id, values }
          await updateCategory({ id: idToSend, values }).unwrap();
        } else {
          await createCategory(values).unwrap();
        }

        // refresh list and single item
        refetchCategories();
        if (isEditMode) refetchCategory();

        // notify parent so it can close modal / refresh
        if (onSaved) onSaved();
      } catch (err: any) {
        const message =
          err?.data?.message || err?.message || "Failed to save category";
        console.error("Save failed:", err);
        setSaveError(String(message));
      }
    },
  });

  // populate form when editing
  useEffect(() => {
    if (isEditMode && categoryData) {
      formik.setValues({
        name: categoryData.name ?? "",
        description: categoryData.description ?? "",
        priority: categoryData.priority ?? 1,
        account_number: categoryData.account_number ?? "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryData, isEditMode]);

  // show loader if editing and the single item is loading (compact = modal)
  if (isEditMode && fetchingCategory && compact) {
    return (
      <div className="text-center py-4">
        <Loader />
      </div>
    );
  }

  // Full-page rendering (if rendered as a route with compact={false})
  if (!compact) {
    return (
      <div className="page-content">
        {saveError && (
          <Row>
            <Col lg={12}>
              <Alert color="danger">{saveError}</Alert>
            </Col>
          </Row>
        )}

        <Row>
          <Col lg={8}>
            <Card>
              <CardHeader>
                <h5 className="card-title mb-0">
                  {isEditMode ? "Edit Category" : "Add Category"}
                </h5>
              </CardHeader>

              <CardBody>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    formik.handleSubmit();
                  }}
                >
                  <div className="mb-3">
                    <Label className="form-label" htmlFor="category-name">
                      Name <span className="text-danger">*</span>
                    </Label>
                    <Input
                      id="category-name"
                      name="name"
                      placeholder="Enter category name"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={!!(formik.touched.name && formik.errors.name)}
                    />
                    {formik.touched.name && formik.errors.name ? (
                      <div className="text-danger mt-1">
                        {formik.errors.name}
                      </div>
                    ) : null}
                  </div>
                  <div className="mb-3">
                    <Label className="form-label" htmlFor="category-priority">
                      Priority (1–3) <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="number"
                      id="category-priority"
                      name="priority"
                      min={1}
                      max={3}
                      value={formik.values.priority}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={!!(formik.touched.priority && formik.errors.priority)}
                    />
                    {formik.touched.priority && formik.errors.priority ? (
                      <div className="text-danger mt-1">{formik.errors.priority}</div>
                    ) : null}
                  </div>

                  <div className="mb-3">
                    <Label
                      className="form-label"
                      htmlFor="category-account-number"
                    >
                      Account Number <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      id="category-account-number"
                      name="account_number"
                      placeholder="Enter account number"
                      value={formik.values.account_number}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={
                        !!(
                          formik.touched.account_number &&
                          formik.errors.account_number
                        )
                      }
                    />
                    {formik.touched.account_number &&
                    formik.errors.account_number ? (
                      <div className="text-danger mt-1">
                        {formik.errors.account_number}
                      </div>
                    ) : null}
                  </div>

                  <div className="mb-3">
                    <Label
                      className="form-label"
                      htmlFor="category-description"
                    >
                      Description
                    </Label>
                    <Input
                      type="textarea"
                      id="category-description"
                      name="description"
                      placeholder="Description"
                      value={formik.values.description}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </div>

                  <div className="text-end">
                    <Button
                      color="primary"
                      type="submit"
                      disabled={creating || updating}
                    >
                      {creating || updating ? (
                        <>
                          <Spinner size="sm" className="me-1" /> Saving...
                        </>
                      ) : isEditMode ? (
                        "Update"
                      ) : (
                        "Create"
                      )}
                    </Button>
                  </div>
                </form>
              </CardBody>
            </Card>
          </Col>

          <Col lg={4}>
            <Card>
              <CardHeader>
                <h5 className="card-title mb-0">Info</h5>
              </CardHeader>
              <CardBody>
                <p className="text-muted">
                  Categories group products (e.g. "Electronics"). Provide a
                  unique name and optional description.
                </p>
                <hr />
                <p className="mb-1">
                  <strong>API shape</strong>
                </p>
                <pre className="small p-2 bg-light rounded">{`{"name":"string","description":"string"}`}</pre>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  // Modal / compact UI
  return (
    <>
      {saveError && (
        <Row>
          <Col lg={12}>
            <Alert color="danger">{saveError}</Alert>
          </Col>
        </Row>
      )}

      <Row>
        <Col lg={12}>
          <Card>
            <CardBody>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  formik.handleSubmit();
                }}
              >
                <div className="mb-3">
                  <Label className="form-label" htmlFor="category-name">
                    Name <span className="text-danger">*</span>
                  </Label>
                  <Input
                    id="category-name"
                    name="name"
                    placeholder="Enter category name"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={!!(formik.touched.name && formik.errors.name)}
                  />
                  {formik.touched.name && formik.errors.name ? (
                    <div className="text-danger mt-1">{formik.errors.name}</div>
                  ) : null}
                </div>
                 <div className="mb-3">
                    <Label className="form-label" htmlFor="category-priority">
                      Priority (1–3) <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="number"
                      id="category-priority"
                      name="priority"
                      min={1}
                      max={3}
                      value={formik.values.priority}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={!!(formik.touched.priority && formik.errors.priority)}
                    />
                    {formik.touched.priority && formik.errors.priority ? (
                      <div className="text-danger mt-1">{formik.errors.priority}</div>
                    ) : null}
                  </div>

                <div className="mb-3">
                  <Label
                    className="form-label"
                    htmlFor="category-account-number"
                  >
                    Account Number <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="text"
                    id="category-account-number"
                    name="account_number"
                    placeholder="Enter account number"
                    value={formik.values.account_number}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={
                      !!(
                        formik.touched.account_number &&
                        formik.errors.account_number
                      )
                    }
                  />
                  {formik.touched.account_number &&
                  formik.errors.account_number ? (
                    <div className="text-danger mt-1">
                      {formik.errors.account_number}
                    </div>
                  ) : null}
                </div>

                <div className="mb-3">
                  <Label className="form-label" htmlFor="category-description">
                    Description
                  </Label>
                  <Input
                    type="textarea"
                    id="category-description"
                    name="description"
                    placeholder="Description"
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>

                <div className="text-end">
                  <Button
                    color="primary"
                    type="submit"
                    disabled={creating || updating}
                  >
                    {creating || updating ? (
                      <>
                        <Spinner size="sm" className="me-1" /> Saving...
                      </>
                    ) : isEditMode ? (
                      "Update"
                    ) : (
                      "Create"
                    )}
                  </Button>
                </div>
              </form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default CategoryEditView;
